import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import MiniCalendar from "components/calendar/MiniCalendar";
import { MdCalendarToday } from "react-icons/md";
import FormField, { getControlClassName, pickTouched } from "components/fields/FormField";

dayjs.extend(customParseFormat);

const TEXT_FORMAT = "MM/DD/YYYY";
const ISO_FORMAT = "YYYY-MM-DD";

/** Formats typed characters as MM/DD/YYYY, keeping only digits. */
const maskDate = (raw) => {
  const digits = raw.replace(/\D/g, "");
  let formatted = digits.slice(0, 2);
  if (digits.length >= 3) formatted += "/" + digits.slice(2, 4);
  if (digits.length >= 5) formatted += "/" + digits.slice(4, 8);
  return formatted;
};

const isoToText = (value) => {
  const parsed = dayjs(value, ISO_FORMAT, true);
  return parsed.isValid() ? parsed.format(TEXT_FORMAT) : "";
};

/**
 * Masked MM/DD/YYYY text input with a calendar popover.
 *
 * Props:
 * - id (auto when omitted), label, required=false, hint, error, touched: see FormField.
 * - state: "error" marks the input invalid without a message.
 * - placeholder="MM/DD/YYYY", name, onBlur, disabled: forwarded to the input.
 * - valueFormat="MM/DD/YYYY": `value` is the input text and onChange receives the masked
 *   text while typing, or a Date when a day is picked from the calendar.
 * - valueFormat="YYYY-MM-DD": `value` is an ISO date and onChange receives ISO dates only.
 *   Typed text stays local until it is a complete, valid date (an invalid complete date
 *   marks the input invalid); leaving the field restores the text of the current value.
 * - className: wrapper classes.
 * - isFutureDateDisabled, isPastDateDisabled and any other props: passed to MiniCalendar.
 */
function DatePicker(props) {
  const {
    children,
    id,
    label,
    required = false,
    hint,
    error,
    touched,
    state,
    placeholder,
    value,
    onChange,
    name,
    onBlur,
    disabled,
    valueFormat = TEXT_FORMAT,
    className = "",
    isFutureDateDisabled,
    isPastDateDisabled,
    ...others
  } = props;
  const ref = useRef();
  const iso = valueFormat === ISO_FORMAT;

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(() => (iso ? isoToText(value) : ""));

  useEffect(() => {
    if (iso) setDraft(isoToText(value));
  }, [iso, value]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const text = iso ? draft : value || "";
  const draftInvalid =
    iso && text.length === TEXT_FORMAT.length && !dayjs(text, TEXT_FORMAT, true).isValid();

  const handleType = (event) => {
    const formatted = maskDate(event.target.value);
    if (!iso) {
      onChange && onChange(formatted);
      return;
    }
    setDraft(formatted);
    const parsed = dayjs(formatted, TEXT_FORMAT, true);
    if (parsed.isValid()) onChange && onChange(parsed.format(ISO_FORMAT));
  };

  const handlePick = (date) => {
    setIsOpen(false);
    if (!onChange) return;
    onChange(iso ? dayjs(date).format(ISO_FORMAT) : date);
  };

  const handleBlur = (event) => {
    if (iso) setDraft(isoToText(value));
    onBlur && onBlur(event);
  };

  return (
    <FormField
      id={id}
      label={label}
      required={required}
      hint={hint}
      error={error}
      {...pickTouched(props)}
      invalid={state === "error" || draftInvalid}
      className={className}
    >
      {(controlProps) => (
        <div
          className="relative"
          ref={ref}
          onKeyDown={(e) => {
            if (e.key === "Escape" && isOpen) setIsOpen(false);
          }}
        >
          <input
            {...controlProps}
            className={getControlClassName({
              invalid: controlProps["aria-invalid"],
              className: "pr-10 md:min-w-[200px]",
            })}
            name={name}
            value={text}
            placeholder={placeholder || TEXT_FORMAT}
            maxLength={10}
            inputMode="numeric"
            required={required}
            disabled={disabled}
            onChange={handleType}
            onBlur={handleBlur}
          />
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            disabled={disabled}
            aria-label="Choose date"
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-fg-muted hover:bg-surface-subtle hover:text-fg focus-ring disabled:cursor-not-allowed disabled:opacity-60"
          >
            <MdCalendarToday aria-hidden="true" size={18} />
          </button>
          {isOpen && (
            <div className="absolute left-0 top-full z-10 mt-1 w-[340px]">
              <MiniCalendar
                isFutureDateDisabled={isFutureDateDisabled}
                isPastDateDisabled={isPastDateDisabled}
                value={text}
                onChange={handlePick}
                {...others}
              />
            </div>
          )}
        </div>
      )}
    </FormField>
  );
}

export default DatePicker;
